import { parse } from 'tldts'
import freeEmailDomainSnapshot from '../../../resources/work_email/free_email_domains.json'

const freeEmailDomains = new Set<string>(freeEmailDomainSnapshot.domains)
const workEmailValidationMessagePrefix = 'Please enter a different email address. This form does not accept addresses from '
// Allow the complete practical HTML/RFC dot-atom character set without accepting quoted local parts.
const workEmailLocalAtomPattern = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+$/
// Keep every domain label within the DNS letter/digit/hyphen shape and 63-character limit.
const workEmailDomainLabelPattern = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/
const workEmailDomainParseOptions = {
  allowIcannDomains: true,
  allowPrivateDomains: false,
  detectIp: true,
  detectSpecialUse: true,
  extractHostname: true,
  mixedInputs: true,
  validHosts: null,
  validateHostname: true,
} as const

export const kWorkEmailInvalidAddressMessage = 'Please enter a valid email address.'

function getValidWorkEmailDomain(normalizedEmail: string): string | undefined {
  // SMTP limits the complete mailbox address to 254 characters in normal use.
  if (normalizedEmail.length > 254) {
    return undefined
  }

  // Require one separator with a non-empty local part; tldts itself parses email-like input leniently.
  const separatorIndex = normalizedEmail.indexOf('@')
  if (separatorIndex <= 0 || separatorIndex !== normalizedEmail.lastIndexOf('@')) {
    return undefined
  }

  const localPart = normalizedEmail.slice(0, separatorIndex)
  const domain = normalizedEmail.slice(separatorIndex + 1)
  // Enforce the local-part length and reject empty atoms from leading, trailing, or repeated dots.
  if (localPart.length > 64 || !localPart.split('.').every((atom) => atom.length > 0 && workEmailLocalAtomPattern.test(atom))) {
    return undefined
  }

  // Reject malformed hostnames before asking tldts to classify their public suffix.
  const domainLabels = domain.split('.')
  if (!domainLabels.every((label) => workEmailDomainLabelPattern.test(label))) {
    return undefined
  }

  return domain
}

function isAcceptedWorkEmailDomain(domain: string): boolean {
  const result = parse(domain, workEmailDomainParseOptions)

  // Ensure the parser treated the complete input as the hostname rather than reinterpreting it as a URL.
  if (result.hostname !== domain) return false
  // Require a registrable name in front of the suffix, rather than a bare suffix such as "com".
  if (result.domain === null) return false
  // Reject unknown or non-ICANN suffixes that HubSpot's Public Suffix List check would not recognize.
  if (result.isIcann !== true) return false
  // Work-email domains must be hostnames, not IPv4 or IPv6 address literals.
  if (result.isIp !== false) return false
  // Exclude reserved namespaces such as localhost, .test, .onion, home.arpa, and example.com.
  return result.isSpecialUse === false
}

export function getBlockedFreeEmailDomain(email: string): string | undefined {
  const normalizedEmail = email.trim().toLowerCase()
  const separatorIndex = normalizedEmail.indexOf('@')

  if (separatorIndex <= 0 || separatorIndex !== normalizedEmail.lastIndexOf('@')) {
    return undefined
  }

  const domain = normalizedEmail.slice(separatorIndex + 1)
  return freeEmailDomains.has(domain) ? domain : undefined
}

export function getBlockedFreeEmailValidationMessage(email: string): string | undefined {
  const blockedDomain = getBlockedFreeEmailDomain(email)
  return blockedDomain ? `${workEmailValidationMessagePrefix}${blockedDomain}.` : undefined
}

export function getWorkEmailValidationMessage(email: string): string | undefined {
  const normalizedEmail = email.trim().toLowerCase()
  if (!normalizedEmail) {
    return undefined
  }

  // Syntax and public-domain failures intentionally share HubSpot's generic validation message.
  const domain = getValidWorkEmailDomain(normalizedEmail)
  if (!domain || !isAcceptedWorkEmailDomain(domain)) {
    return kWorkEmailInvalidAddressMessage
  }

  // Apply the existing free/disposable-provider policy only after the address itself is valid.
  return getBlockedFreeEmailValidationMessage(normalizedEmail)
}
